import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addHours, subMinutes } from 'date-fns';
import { getMessageError, getMessageErrorByInvalidSession } from '../utils';
import { setAuthorizationToken } from '../services/api';
import {
    authenticate,
    findAllDistributions,
    findAllExhibitors,
    findAllExhibitorsByBalance,
    findAllParameters,
    findAllTemplates,
    getUserDetails
} from '../services';
import {
    useBalance,
    useExhibitor,
    useLoader,
    useParameter,
    usePopup,
    useStatement,
    useTemplate,
    useToast
} from '../hooks';
import { Balance, CustomError, CustomResponse, Exhibitor, Login, Parameter, Statement, Template, UserSession } from '../dtos';
import { addToLocalStorage, clearFromLocalStorage, getFromLocalStorage } from '../store';

type AllData = {
    exhibitors: Exhibitor[];
    balances: Balance[] | never[];
    statements: Statement[];
    templates: Template[];
    parameters: Parameter | null;
}

type State = {
    user: UserSession | null;
    authenticateUser: (login: Login) => Promise<void>;
    editUser: (user: UserSession) => void;
    handleRequest: (request: () => Promise<void>) => Promise<CustomResponse>;
    loadAllData: (user: UserSession) => Promise<AllData | null>;
    logout: () => void;
};

type AuthenticatedState = State & {
    user: UserSession;
};

type Props = {
    children: React.ReactNode;
};

const SessionContext = createContext<State>({} as State);

const SessionProvider: React.FC<Props> = ({ children }) => {
    const navigate = useNavigate();
    const { showLoading, closeLoading } = useLoader();
    const { showToast } = useToast();
    const { closePopup, showPopup } = usePopup();
    const { setExhibitors } = useExhibitor();
    const { setBalances } = useBalance();
    const { setStatements } = useStatement();
    const { setTemplates } = useTemplate();
    const { setParameters } = useParameter();
    const [isRefresh, setRefresh] = useState<boolean>(false);
    const [user, setUser] = useState<UserSession | null>(() => {
        setRefresh(true);
        return getFromLocalStorage({
            module: 'session',
            key: 'user',
            defaultValue: null
        });
    });

    const logout = useCallback(() => {
        clearFromLocalStorage({
            module: 'session',
            key: 'user'
        });
        setAuthorizationToken(null);
        setUser(null);
        closePopup();
        navigate('/', {
            replace: true
        });
    }, [navigate, closePopup]);

    const handleRequest = useCallback
        (async (request: () => Promise<void>, defaultMessage?: string): Promise<CustomResponse> => {
            try {
                showLoading();
                await request();
                return {
                    success: true
                };
            } catch (err) {
                let customError: CustomError | null = getMessageErrorByInvalidSession({
                    err,
                    defaultMessage,
                    user
                });

                if (customError) {
                    if (customError.type === 'SESSION_INVALID') {
                        showPopup({
                            message: customError.message,
                            description: 'Você será deslogado da aplicação.',
                            handleOk: () => logout()
                        });
                    } else {
                        logout();
                        showToast({
                            type: 'error',
                            message: `${customError.message} ${user ? 'Você será deslogado da aplicação.' : ''}`
                        });
                    }

                    return {
                        success: false,
                        error: customError
                    };
                }

                let payload: object = {};

                if (user) {
                    const { id, name, email, tokenExpiredDate } = user;
                    payload = {
                        user: {
                            id,
                            name,
                            email,
                            tokenExpiredDate
                        }
                    };
                }

                customError = getMessageError({
                    payload: {
                        type: 'Webapp',
                        url: window.location.href,
                        ...payload
                    },
                    err,
                    defaultMessage
                });

                if (customError.type === 'UNKNOWN')
                    showToast({
                        type: 'error',
                        message: customError.message
                    });

                return {
                    success: false,
                    error: customError
                };
            } finally {
                closeLoading();
            }
        }, [showLoading, closeLoading, showToast, showPopup, logout, user]);

    const loadAllData = useCallback(async (user: UserSession): Promise<AllData | null> => {
        let allData: AllData | null = null;

        await handleRequest(async () => {
            const [exhibitors, balances, statements, templates, parameters] = await Promise.all([
                findAllExhibitors(),
                user.hasBalancePermission ? findAllExhibitorsByBalance() : [],
                findAllDistributions(),
                findAllTemplates(),
                findAllParameters()
            ]);

            allData = {
                exhibitors,
                balances,
                statements,
                templates,
                parameters
            };

            setExhibitors(exhibitors);
            setBalances(balances);
            setStatements(statements);
            setTemplates(templates);
            setParameters(parameters);
        });

        return allData;
    }, [handleRequest, setExhibitors, setBalances, setStatements, setTemplates, setParameters]);

    useEffect(() => {
        if (user && isRefresh) {
            setAuthorizationToken(user.token);
            loadAllData(user);
            setRefresh(false);
        }
    }, [loadAllData, user, isRefresh])

    const authenticateUser = useCallback(async (login: Login) => {
        await handleRequest(async () => {
            const { token, isBlocked, isFirstLogin, hasBalancePermission } = await authenticate(login);
            if (isBlocked === 'true') {
                showToast({
                    type: 'error',
                    message: 'Você está bloqueado, entre em contato com o administrador'
                });
            } else if (isFirstLogin === 'true') {
                showToast({
                    type: 'error',
                    message: 'Este é seu primeiro login, é necessário trocar a senha'
                });

                navigate('/reset-password', {
                    replace: true,
                    state: login
                });
            } else {
                setAuthorizationToken(token);
                const user = await getUserDetails();
                const userSession: UserSession = {
                    ...user,
                    hasBalancePermission: hasBalancePermission === 'true',
                    token,
                    tokenExpiredDate: addHours(subMinutes(new Date(), 2), 20)
                };
                await loadAllData(userSession);
                addToLocalStorage({
                    module: 'session',
                    key: 'user',
                    data: userSession
                });
                setUser(userSession);
                showToast({
                    type: 'success',
                    message: 'Login efetuado com sucesso'
                });
                navigate('/home', {
                    replace: true
                });
            }
        }, 'Credenciais inválidas, verifique o email e a senha!');
    }, [handleRequest, navigate, showToast, loadAllData]);

    const editUser = useCallback((user: UserSession) => setUser(user), []);

    return (
        <SessionContext.Provider
            value={{
                user,
                authenticateUser,
                editUser,
                handleRequest,
                logout,
                loadAllData
            }}
        >
            {children}
        </SessionContext.Provider>
    );
};

const useSession = (): State => {
    const context = useContext(SessionContext);

    return context;
}

const useAuthenticatedSession = (): AuthenticatedState => {
    const context = useContext(SessionContext);

    const { user, ...rest } = context;

    if (user === null)
        throw new Error('The user state in the useAuthenticatedSession cannot be null');

    return {
        ...rest,
        user
    };
}


export { SessionProvider, useSession, useAuthenticatedSession };